import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * URL-safe slug: lowercase, ASCII letters/digits, single dashes. Follows the API's slugify for
 * tech names ("C++ vs C#" → "cpp-vs-csharp", "ASP.NET" → "asp-dotnet", "Next.js" → "nextjs"),
 * so the slug shown in the editor is the one the server stores.
 */
export function slugify(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/c\+\+/g, ' cpp ')
    .replace(/c#/g, ' csharp ')
    .replace(/f#/g, ' fsharp ')
    .replace(/\.net\b/g, ' dotnet ')
    .trim()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
const fullNumber = new Intl.NumberFormat('en')

export function formatNumber(value: number | null | undefined, compact = false) {
  if (value == null || Number.isNaN(value)) return '—'
  return compact ? compactNumber.format(value) : fullNumber.format(value)
}

export function initials(name?: string | null) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('') || '?'
}

/**
 * Parses API timestamps ("2026-01-02 10:00:00" or ISO) into a Date, or null.
 * The API stores UTC, so a timestamp without a zone suffix is read as UTC, not local time.
 */
export function parseDate(value?: string | null) {
  if (!value) return null
  const iso = value.includes('T') ? value : value.replace(' ', 'T')
  const hasTime = /T\d/.test(iso)
  const hasZone = /([zZ]|[+-]\d\d:?\d\d)$/.test(iso)
  const date = new Date(hasTime && !hasZone ? `${iso}Z` : iso)
  return Number.isNaN(date.getTime()) ? null : date
}
